/**
 * The four agreement forms, with the endings picked out.
 *
 * The sibling of the Verbos conjugation table, and there for the same reason:
 * `novo / nova / novos / novas` is one pattern, not four words, and the
 * difference between those two readings is whether the ending is visible.
 *
 * An invariable word gets a sentence saying so instead of a table. That is the
 * more useful thing to print: the mistake a German speaker makes here is
 * inventing a feminine for `grande`, and a blank table would not warn her.
 */
import { cn } from '@/lib/utils';

import type { AdjetivoPayload } from './types';

const LABELS = ['masculino', 'feminino', 'masc. plural', 'fem. plural'] as const;

/** Split a form against its masculine singular, so only what changed lights up. */
function split(form: string, base: string): [string, string] {
  let shared = 0;
  while (shared < form.length && shared < base.length && form[shared] === base[shared]) shared++;
  // `bom` → `boa` shares only `bo`; `novo` → `nova` shares `nov`. Either way
  // the tail is what the learner has to change.
  return [form.slice(0, shared), form.slice(shared)];
}

export function AgreementRow({ payload }: { payload: AdjetivoPayload }) {
  if (payload.pos === 'adverb') {
    return (
      <p className="border-border text-muted-foreground rounded-lg border p-4 text-sm">
        An adverb — it never changes.
      </p>
    );
  }

  if (payload.invariable || !payload.forms) {
    return (
      <div className="border-border flex flex-col gap-1 rounded-lg border p-4">
        <p className="text-muted-foreground text-sm">
          Same for masculine and feminine — there is no feminine form to make.
        </p>
        {/*
          But it still has a plural, and saying only "invariable" taught that it
          did not. `fácil` becomes `fáceis`, `azul` becomes `azuis`.
        */}
        {payload.plural && (
          <p className="text-sm">
            <span className="text-muted-foreground">Plural: </span>
            <span className="font-display text-lg">{payload.plural}</span>
          </p>
        )}
      </div>
    );
  }

  const [base] = payload.forms;

  return (
    <div className="border-border flex flex-col gap-2 rounded-lg border p-4">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        Concordância
      </span>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
        {payload.forms.map((form, i) => {
          const [stem, ending] = split(form, base);
          return (
            <div key={form} className="flex flex-col">
              <span className="text-muted-foreground text-xs">{LABELS[i]}</span>
              <span className="font-display text-lg">
                <span className="text-muted-foreground">{stem}</span>
                <span className={cn('text-primary bg-primary/10 rounded-sm px-0.5 font-semibold')}>
                  {ending}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
